/**
 * FlexPay API-level encryption + signing.
 *
 * Uses Web Crypto API (built-in, no external dependencies):
 *   - AES-256-GCM for encryption
 *   - HMAC-SHA256 for signing
 *   - PBKDF2 for key derivation from passphrase
 *
 * Envelope format (matches backend exactly):
 *   { encrypted: true, payload: base64, nonce: base64, timestamp: ISO, signature: base64 }
 */

const PASSPHRASE = "18 oct 2001";
const ENC_SALT = "payflex_enc_salt_v1";
const SIG_SALT = "payflex_sig_salt_v1";
const ITERATIONS = 100_000;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// ---------------------------------------------------------------------------
// Key cache — derive once, reuse forever
// ---------------------------------------------------------------------------
let _cachedKeys = null;

async function getKeys() {
  if (_cachedKeys) return _cachedKeys;

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(PASSPHRASE),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  const encKey = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(ENC_SALT),
      iterations: ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );

  const sigKey = await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(SIG_SALT),
      iterations: ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "HMAC", hash: "SHA-256", length: 256 },
    false,
    ["sign", "verify"]
  );

  _cachedKeys = { encKey, sigKey };
  return _cachedKeys;
}

// ---------------------------------------------------------------------------
// Helpers: ArrayBuffer ↔ Base64
// ---------------------------------------------------------------------------
function bufToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuf(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// Sign / Verify
// ---------------------------------------------------------------------------
async function sign(payloadB64, nonceB64, timestamp) {
  const { sigKey } = await getKeys();
  const data = encoder.encode(payloadB64 + nonceB64 + timestamp);
  const sig = await crypto.subtle.sign("HMAC", sigKey, data);
  return bufToBase64(sig);
}

async function verify(payloadB64, nonceB64, timestamp, signatureB64) {
  const { sigKey } = await getKeys();
  const data = encoder.encode(payloadB64 + nonceB64 + timestamp);
  const sig = base64ToBuf(signatureB64);
  return crypto.subtle.verify("HMAC", sigKey, sig, data);
}

// ---------------------------------------------------------------------------
// Encrypt / Decrypt
// ---------------------------------------------------------------------------
export async function encryptPayload(data) {
  const { encKey } = await getKeys();
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = encoder.encode(JSON.stringify(data));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    encKey,
    plaintext
  );

  const payloadB64 = bufToBase64(ciphertext);
  const nonceB64 = bufToBase64(nonce);
  const timestamp = new Date().toISOString();
  const signature = await sign(payloadB64, nonceB64, timestamp);

  return {
    encrypted: true,
    payload: payloadB64,
    nonce: nonceB64,
    timestamp,
    signature,
  };
}

export async function decryptPayload(envelope) {
  if (!envelope || !envelope.encrypted) return envelope;

  const { payloadB64, nonceB64, timestamp, signature } =
    typeof envelope.payload === "string"
      ? {
          payloadB64: envelope.payload,
          nonceB64: envelope.nonce,
          timestamp: envelope.timestamp,
          signature: envelope.signature,
        }
      : envelope;

  // Verify HMAC signature
  const valid = await verify(payloadB64, nonceB64, timestamp, signature);
  if (!valid) {
    throw new Error("Invalid server signature — response rejected");
  }

  // Decrypt AES-GCM
  const { encKey } = await getKeys();
  const nonce = base64ToBuf(nonceB64);
  const ciphertext = base64ToBuf(payloadB64);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce },
    encKey,
    ciphertext
  );

  return JSON.parse(decoder.decode(plaintext));
}

/**
 * Check if a response is an encrypted envelope.
 */
export function isEncryptedEnvelope(data) {
  return (
    data &&
    typeof data === "object" &&
    data.encrypted === true &&
    typeof data.payload === "string" &&
    typeof data.nonce === "string" &&
    typeof data.signature === "string"
  );
}

// Pre-warm key derivation on module load (async, non-blocking)
getKeys().catch(() => {});
